import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';
import { Logo } from '@/components/Logo';
import {
  LayoutDashboard,
  Database,
  FolderOpen,
  FileText,
  Settings,
  ExternalLink,
  Handshake,
  MessageSquare,
  Search,
  Library,
  ListFilter,
  Lightbulb,
  BarChart3,
  FileType,
  Code2,
  Users,
  LayoutTemplate,
  User,
  History,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const dataArchItems = [
  { id: 'data-type-docs', title: 'Data Types', icon: FileType },
  { id: 'field-library', title: 'Field Library', icon: Library },
  { id: 'lookups', title: 'Lookups', icon: ListFilter },
  { id: 'adf-elements', title: 'ADF Elements', icon: Code2 },
  { id: 'adf-templates', title: 'ADF Templates', icon: LayoutTemplate },
  { id: 'adf-changelog', title: 'ADF Changelog', icon: History },
];

const contentItems = [
  { id: 'standards', title: 'Data Standards', icon: Database },
  { id: 'categories', title: 'Categories', icon: FolderOpen },
  { id: 'partners', title: 'Partners', icon: Handshake },
];

const docsItems = [
  { id: 'doc-categories', title: 'Categories', icon: FolderOpen },
  { id: 'docs', title: 'Articles', icon: FileText },
];

const systemItems = [
  { id: 'users', title: 'Users', icon: Users },
  { id: 'analytics', title: 'Analytics', icon: BarChart3 },
  { id: 'proposals', title: 'Proposals', icon: Lightbulb },
  { id: 'submissions', title: 'Submissions', icon: MessageSquare },
  { id: 'seo', title: 'Page SEO', icon: Search },
  { id: 'settings', title: 'Site Settings', icon: Settings },
  { id: 'account', title: 'My Account', icon: User },
];

// Extract section from URL path
function getActiveSectionFromPath(pathname: string): string {
  const path = pathname.replace('/dashboard', '').replace(/^\//, '');
  return path || 'overview';
}

export function AdminSidebar() {
  const { state, setOpenMobile, isMobile } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const collapsed = state === 'collapsed';

  // Derive active section from URL
  const activeSection = getActiveSectionFromPath(location.pathname);

  const handleSectionChange = (sectionId: string) => {
    if (sectionId === 'overview') {
      navigate('/dashboard');
    } else {
      navigate(`/dashboard/${sectionId}`);
    }
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-border">
      <SidebarHeader className="border-b border-border px-4 h-14 flex items-center">
        <Link to="/dashboard" className="flex items-center gap-2">
          <Logo size="sm" />
          {!collapsed && (
            <span className="font-semibold text-sm">Admin</span>
          )}
        </Link>
      </SidebarHeader>

      <SidebarContent>
        {/* Overview */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  isActive={activeSection === 'overview'}
                  onClick={() => handleSectionChange('overview')}
                  tooltip="Overview"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  <span>Overview</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Content */}
        <SidebarGroup>
          <SidebarGroupLabel>Content</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {contentItems.map((item) => (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    isActive={activeSection === item.id}
                    onClick={() => handleSectionChange(item.id)}
                    tooltip={item.title}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Data Architecture */}
        <SidebarGroup>
          <SidebarGroupLabel>Data Architecture</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {dataArchItems.map((item) => (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    isActive={activeSection === item.id}
                    onClick={() => handleSectionChange(item.id)}
                    tooltip={item.title}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Docs */}
        <SidebarGroup>
          <SidebarGroupLabel>Documentation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {docsItems.map((item) => (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    isActive={activeSection === item.id}
                    onClick={() => handleSectionChange(item.id)}
                    tooltip={item.title}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* System */}
        <SidebarGroup>
          <SidebarGroupLabel>System</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {systemItems.map((item) => (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    isActive={activeSection === item.id}
                    onClick={() => handleSectionChange(item.id)}
                    tooltip={item.title}
                  >
                    <item.icon className="h-4 w-4" />
                    <span>{item.title}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-border p-2">
        <Button variant="ghost" size="sm" asChild className="w-full justify-start">
          <Link to="/api-standards">
            <ExternalLink className="h-4 w-4 mr-2" />
            {!collapsed && <span>View Public Site</span>}
          </Link>
        </Button>
      </SidebarFooter>
    </Sidebar>
  );
}
